// furigana.js — CORRECT, context-aware furigana (ルビ) via a real morphological
// analyzer (kuromoji).
//
// 2026-07-17 (PI ruling): the earlier pre-generated word+per-kanji table produced
// WRONG readings for words outside the table (今年→いまねん not ことし; 中学生's 生→
// なま not せい) — unacceptable over the research stimulus (uploaded problem text) and
// pedagogically harmful. This engine delegates to kuromoji: readings come from a real
// dictionary + morphological segmentation, so they are correct and context-aware with
// full coverage. Never a guess.
//
// The tokenizer loads ASYNCHRONOUSLY (see FuriganaProvider, which builds it once and
// calls setFuriganaTokenizer). Until it is ready, annotateFurigana returns the plain
// string with NO reading — we never display an unverified reading. The segment output
// shape is unchanged ({text} | {base, reading}) so FuriganaText / svgFurigana /
// furiganaAria consume it without change.

let _tokenizer = null;

/** Install the built kuromoji tokenizer (called once by FuriganaProvider / tests). */
export function setFuriganaTokenizer(tokenizer) { _tokenizer = tokenizer; }
export function getFuriganaTokenizer() { return _tokenizer; }
export function isFuriganaReady() { return _tokenizer != null; }

// CJK ideograph, including compatibility glyphs and supplementary-plane extensions
// used in real Japanese names (𠮷/﨑/神/髙). Kana / latin / punctuation → false.
export function isKanjiChar(ch) {
  if (!ch) return false;
  const c = ch.codePointAt(0);
  return (c >= 0x4e00 && c <= 0x9fff)
    || (c >= 0x3400 && c <= 0x4dbf)
    || (c >= 0xf900 && c <= 0xfaff)
    || (c >= 0x20000 && c <= 0x2fa1f)
    || (c >= 0x30000 && c <= 0x323af);
}

// Katakana → hiragana (kuromoji readings are katakana).
function kataToHira(s) {
  return s.replace(/[ァ-ヶ]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0x60));
}

// Number + counter kanji: kuromoji reads a lone counter with its NOUN reading, so a
// stable regular counter after digits gets its correct reading here. Only counters
// whose after-number reading is INVARIANT are listed; anything else after a number is
// left bare rather than risk a wrong reading. 月 is deliberately EXCLUDED: its reading
// is context-dependent and unresolvable (calendar いちがつ vs duration いっかげつ/ひとつき
// vs compound げつまつ/つきごろ), so every 月 right after a number is left bare — never a
// wrong reading. Applied only when the token is a lone kanji following digits.
const COUNTER_READINGS = {
  年: 'ねん', 時: 'じ', 円: 'えん', 度: 'ど', 週: 'しゅう', 回: 'かい',
  番: 'ばん', 点: 'てん', 割: 'わり', 秒: 'びょう', 歳: 'さい', 才: 'さい', 名: 'めい',
  位: 'い', 倍: 'ばい', 章: 'しょう', 問: 'もん', 件: 'けん', 個: 'こ', 軸: 'じく',
};
const NUMERIC_TOKEN_RE = /^[0-9０-９]+$/;

// VERIFIED surface overrides for words IPADIC mis-reads (e.g. mis-parses a common
// compound as a proper name). Small, curated, hand-checked; extend as real
// participant strings surface a wrong reading. Exact whole-token surface → hiragana.
const OVERRIDE_READINGS = {
  高品質: 'こうひんしつ', // kuromoji parses this as the surname 高品(たかしな) + 質
  棒グラフ: 'ぼうぐらふ', // a bar chart. After a bullet ("・棒グラフ") IPADIC splits the compound and mis-reads the
  // lone 棒 as the surname ささげ; matching the whole 棒グラフ compound keeps ぼう. This is narrower than the old
  // bare 棒→ぼう override, which also fired on a genuine surname 棒(ささげ)さん in uploaded stimulus (Codex R16 #5).
  樹形図: 'じゅけいず', // kuromoji: 樹(き)形(がた)図 → tree diagram is じゅけいず
  重なり: 'かさなり', // "集合の重なり" → kuromoji splits 重(じゅう)なり; overlap is かさなり
  下マージン: 'したまーじん', // 下 in a margin label is した, not しも (下側 is already correct)
  答疑: 'とうぎ', // Q&A term: kuromoji splits 答(こたえ)疑(うたぐ)
  誤答: 'ごとう', // wrong-answer: kuromoji splits 誤(あやま)答(こたえ)
  偏好: 'へんこう', // preference (呈現モード偏好): kuromoji splits 偏(へん)好(よしみ)
  四分位: 'しぶんい', // quartile (四分位数 → 四分位(しぶんい)+数): kuromoji reads 四(よん)分(ふん)位(い)
  題幹: 'だいかん', // question stem: kuromoji splits 題(だい)幹(みき)
  図形: 'ずけい', // figure/shape: IPADIC voices it as ヅケイ; correct is ずけい
  視空間: 'しくうかん', // visuospatial (視空間処理): kuromoji reads 視 as み (視る); here 視=し
  空集合: 'くうしゅうごう', // empty set (set-theory term, cf. ベン図/集合): kuromoji reads 空 as そら
  彩度: 'さいど', // saturation (高彩度 → 高(こう)+彩度): kuromoji reads 彩(いろどり)度(ど)
  無彩色: 'むさいしょく', // achromatic (color preset): kuromoji reads the 彩色 token as さいしき (paint sense)
  伊藤啓: 'いとうけい', // cited CUD-guideline author "Kei Ito" (en locale): kuromoji reads 啓 as あきら
};
const OVERRIDE_KEYS = Object.keys(OVERRIDE_READINGS);

// Verified never-guess proper names: non-Japanese personal names with no canonical hiragana reading.
// kuromoji WILL emit a (wrong) guess, so we render them bare instead — a guessed name reading violates
// the PI "never display an unverified reading" ruling. A name WITH a verifiable reading (e.g. 伊藤啓 =
// "Kei Ito" from the en locale) goes in OVERRIDE_READINGS above, not here. 葉豊 = the developer "Ye Feng"
// (en locale) — a Chinese name with no established Japanese reading, so bare.
// Exported so the real-i18n zero-miss gate treats these as documented intentional-bare (same
// class as native <option> text), not as unwrapped chrome.
export const BARE_SURFACES = ['葉豊'];

// A dynamic name is not safe merely because IPADIC can read part of it as an ordinary word
// (小鳥遊さん -> 小鳥(ことり) + bare 遊, although the surname is たかなし). When a
// contiguous kanji span is immediately followed by a person-name suffix AND IPADIC leaves part
// of that span unread, keep the WHOLE span bare rather than display a misleading partial reading.
// A suffix alone is not proof of a name: ordinary dictionary-readable roles such as 利用者さん /
// お医者さん keep their useful readings. Structured app identities are bared at their interpolation
// sites independently, so this fallback is only for the unsafe "partially unread" stimulus case.
// Candidate boundaries are derived from TOKENS around the suffix, not a greedy character regex: the latter
// both swallowed preceding words (生徒小鳥遊さん -> 生徒 became bare) and missed mixed-script names
// (小鳥遊たかしさん / 小鳥遊・太郎さん -> false partial ことり). A verified whole-word override
// (e.g. 伊藤啓) remains authoritative.
const PERSON_NAME_SUFFIX_READINGS = new Map([
  ['さん', null], ['さま', null], ['様', 'さま'], ['くん', null], ['君', 'くん'],
  ['ちゃん', null], ['先生', 'せんせい'], ['氏', 'し'], ['殿', 'どの'],
]);
const PERSON_NAME_SUFFIXES = new Set(PERSON_NAME_SUFFIX_READINGS.keys());
const NAME_KANJI_TOKEN_RE = /^[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\u{20000}-\u{2fa1f}\u{30000}-\u{323af}々〆ヵヶ]+$/u;
const NAME_JOINER_RE = /^[・･]$/u;
const UNREAD_NAME_TOKEN_RE = /^[ぁ-ゖァ-ヺーA-Za-zＡ-Ｚａ-ｚ0-9０-９]+$/u;
const READABLE_KANA_NAME_TOKEN_RE = /^[ぁ-ゖァ-ヺー]+$/u;
const VERIFIED_PERSON_NAME_SURFACES = new Set(['伊藤啓']);

function hasTokenReading(tk) {
  return Boolean(tk && tk.reading && tk.reading !== '*');
}

function hasIndependentSurfaceReading(surface) {
  if (!_tokenizer || !surface) return false;
  let parts;
  try { parts = _tokenizer.tokenize(surface); } catch (e) { return false; }
  if (!parts || parts.length === 0
    || parts.map((part) => (part && part.surface_form) || '').join('') !== surface) return false;
  return parts.every((part) => {
    const partSurface = (part && part.surface_form) || '';
    return !Array.from(partSurface).some(isKanjiChar) || hasTokenReading(part);
  });
}

function isDictionaryHumanName(tk) {
  return Boolean(tk && tk.pos_detail_1 === '固有名詞' && tk.pos_detail_2 === '人名'
    && hasTokenReading(tk));
}

function isPersonSuffixToken(tk) {
  const surface = (tk && tk.surface_form) || '';
  return PERSON_NAME_SUFFIXES.has(surface)
    || Boolean(tk && tk.pos_detail_1 === '接尾' && tk.pos_detail_2 === '人名');
}

function findPersonSuffixAnchors(text, tokens, tokenSpans) {
  const byStart = new Map();
  const add = (start, end, suffix, reading = PERSON_NAME_SUFFIX_READINGS.get(suffix)) => {
    if (start <= 0 || end <= start || byStart.has(start)) return;
    byStart.set(start, { start, end, suffix, reading });
  };

  // Prefer the analyzer's semantic person-suffix classification. This includes 殿,
  // which IPADIC marks 接尾-人名 even though the earlier surface allow-list omitted it.
  tokens.forEach((tk, index) => {
    if (isPersonSuffixToken(tk)) {
      add(
        tokenSpans[index].start,
        tokenSpans[index].end,
        tk.surface_form || '',
        hasTokenReading(tk) ? kataToHira(tk.reading) : undefined
      );
    }
  });

  // IPADIC sometimes absorbs or splits the honorific (遊君 / たさ|ん). Anchor the
  // supported literal suffix in the SOURCE as a conservative fallback. A following
  // kanji means the match is inside a lexical word (様子 / 君臨), not an honorific.
  for (const suffix of PERSON_NAME_SUFFIXES) {
    let from = 0;
    while (from < text.length) {
      const start = text.indexOf(suffix, from);
      if (start < 0) break;
      const end = start + suffix.length;
      const nextChar = text.slice(end, end + 1);
      if (!isKanjiChar(nextChar)) add(start, end, suffix);
      from = start + Math.max(1, suffix.length);
    }
  }
  return [...byStart.values()].sort((a, b) => a.start - b.start);
}

function findNameContexts(text, tokens, tokenSpans) {
  const ranges = [];
  for (const suffixAnchor of findPersonSuffixAnchors(text, tokens, tokenSpans)) {
    let rightToken = tokenSpans.findIndex((span) => span.start < suffixAnchor.start && span.end >= suffixAnchor.start);
    if (rightToken < 0) rightToken = tokenSpans.findIndex((span) => span.start >= suffixAnchor.start) - 1;
    while (rightToken >= 0 && tokenSpans[rightToken].start >= suffixAnchor.start) rightToken -= 1;
    while (rightToken >= 0 && text.slice(
      tokenSpans[rightToken].start,
      Math.min(tokenSpans[rightToken].end, suffixAnchor.start)
    ).trim() === '') rightToken -= 1;
    if (rightToken < 0) continue;

    let startToken = -1;
    let hasUnresolvedToken = false;
    let lexicalRightUsed = false;
    let lexicalLeftUsed = false;
    let hasReadableKanjiTail = false;
    for (let i = rightToken; i >= 0; i -= 1) {
      const tk = tokens[i];
      const fullSurface = (tk && tk.surface_form) || '';
      // The source suffix may begin inside a token (遊君 / たさ). Only the source
      // characters before that boundary belong to the candidate name.
      const surface = text.slice(
        tokenSpans[i].start,
        Math.min(tokenSpans[i].end, suffixAnchor.start)
      );
      if (surface.trim() === '') continue; // allow an internal name space; excluded from the final edge offsets

      const partialToken = surface !== fullSurface;
      const human = !partialToken && isDictionaryHumanName(tk);
      const joiner = NAME_JOINER_RE.test(surface);
      const kanji = NAME_KANJI_TOKEN_RE.test(surface);
      // A kanji fragment cut from an absorbed honorific token cannot inherit the
      // full token's reading (遊君=ゆうくん). Re-tokenize only that fragment:
      // 遊 remains unresolved, while lexical 主 from 主君 has its own verified しゅ reading.
      const partialKanjiUnread = partialToken && kanji && !hasIndependentSurfaceReading(surface);
      const unread = (!hasTokenReading(tk) || partialKanjiUnread)
        && (kanji || UNREAD_NAME_TOKEN_RE.test(surface));
      const lexicalPrefix = hasTokenReading(tk) && tk && tk.pos === '接頭詞';
      // A tokenizer may split a long unknown surname into many readable single-Han
      // 一般 tokens plus one unread token. Keep those name-compatible pieces without
      // a length cap while still enforcing the one-readable-bridge budget once the
      // unresolved core has consumed its surname-side bridge. This keeps an adjacent
      // one-kanji role/prose token (男/兄/母/娘) outside the bare name span. Lexical
      // prefixes and one-kanji pronouns/adverbial nouns remain boundaries as before.
      const nameLikeSingleKanji = kanji && Array.from(surface).length === 1
        && !(hasUnresolvedToken && lexicalLeftUsed)
        && !lexicalPrefix && tk && (tk.pos_detail_1 === '一般' || tk.pos_detail_1 === '接尾');
      const readableKanaNamePart = !hasUnresolvedToken && hasTokenReading(tk)
        && READABLE_KANA_NAME_TOKEN_RE.test(surface) && !lexicalPrefix;
      // Kana/non-Han lexical suffixes remain boundaries. A Han suffix is allowed as
      // a candidate tail because IPADIC tags real surnames such as 髙|橋|さん with
      // 橋=接尾-一般. Without an unresolved neighbour (利用|者|さん), the
      // range is not guarded and ordinary lexical readings remain unchanged.
      const lexicalSuffix = tk && tk.pos_detail_1 === '接尾' && !human;
      if (lexicalPrefix || (lexicalSuffix && !kanji)
        || (!human && !joiner && !kanji && !unread && !readableKanaNamePart)) break;

      if (!hasUnresolvedToken && kanji && hasTokenReading(tk) && !human && !partialToken) {
        hasReadableKanjiTail = true;
      }

      if (human || joiner || unread || nameLikeSingleKanji || readableKanaNamePart) {
        startToken = i;
        if (unread) {
          hasUnresolvedToken = true;
          // 𠮷|田 and 髙|橋 have a readable Han tail to the right of the
          // unresolved glyph. That tail already supplies the surname-side bridge, so
          // do not absorb adjacent prose on the left (生徒|𠮷|田|さん).
          if (hasReadableKanjiTail) lexicalLeftUsed = true;
        }
        continue;
      }

      // A partially unknown surname commonly tokenizes as one readable lexical token plus an unread tail
      // (小鳥|遊). Permit ONE readable bridge on each side of the unread core, including a one-kanji
      // name component. Stopping before a second bridge preserves adjacent prose/roles: 生徒|小鳥|遊
      // keeps 生徒 annotated, and 私|𠮷|田 does not swallow the one-kanji pronoun once 田 has
      // already consumed the surname-side bridge.
      if (hasUnresolvedToken ? !lexicalLeftUsed : !lexicalRightUsed) {
        startToken = i;
        if (hasUnresolvedToken) lexicalLeftUsed = true;
        else lexicalRightUsed = true;
        continue;
      }
      break;
    }
    if (startToken < 0) continue;

    const start = tokenSpans[startToken].start;
    const end = suffixAnchor.start;
    const name = text.slice(start, end).trim();
    const candidateTokens = tokens.slice(startToken, rightToken + 1)
      .filter((tk) => ((tk && tk.surface_form) || '').trim() !== ''
        && !NAME_JOINER_RE.test((tk && tk.surface_form) || ''));
    const boundaryCutsToken = tokenSpans[rightToken].end > suffixAnchor.start;
    if (boundaryCutsToken) {
      // A literal suffix inside ONE readable lexical token is not enough evidence:
      // 模様/仕様/主君/彼氏/宮殿 are ordinary words, not name + honorific. The
      // absorbed-honorific recovery is only safe when the candidate already spans
      // multiple tokens (小鳥|遊君 / 小鳥|遊|ひな|たさ|ん).
      if (startToken === rightToken) continue;

      // When the complete suffix sits inside the cut token, its dictionary reading
      // must actually end in the verified honorific reading. This preserves lexical
      // compounds such as 確認仕様 while accepting 遊君=ゆうくん and けんさん.
      const rightTk = tokens[rightToken];
      const suffixWhollyInsideRightToken = suffixAnchor.end <= tokenSpans[rightToken].end;
      const suffixReading = suffixAnchor.reading ?? PERSON_NAME_SUFFIX_READINGS.get(suffixAnchor.suffix);
      if (suffixWhollyInsideRightToken && suffixReading && hasTokenReading(rightTk)
        && !kataToHira(rightTk.reading).endsWith(suffixReading)) continue;
    }
    const dictionaryVerified = !boundaryCutsToken && candidateTokens.length > 0
      && candidateTokens.every(isDictionaryHumanName);
    const externallyVerified = VERIFIED_PERSON_NAME_SURFACES.has(name);
    ranges.push({
      start, end, startToken, endToken: rightToken + 1, name,
      suffix: suffixAnchor.suffix,
      suffixReading: suffixAnchor.reading,
      suffixStart: suffixAnchor.start,
      suffixEnd: suffixAnchor.end,
      dictionaryVerified, hasUnresolvedToken, externallyVerified,
    });
  }
  return ranges;
}

// NOTE (方案C, Codex R16): this engine also runs over uploaded research stimulus (problem text / chart
// labels / chat), so it holds ONLY rules that are correct in EVERY context. Readings that are right for a
// SPECIFIC UI label but wrong on dynamic text are NOT here — they were removed after Codex R16 #1 showed a
// single ambiguous surface fires the UI reading on dynamic labels too (the size 中=ちゅう rule also forced a
// chart-category 中 that should read なか). Each such label now supplies its own reading AT ITS RENDER SITE
// via <FuriganaText overrides={{surface: reading}}>, so the ambiguity is resolved by the component that
// knows the label — not guessed by an engine that also sees stimulus. Removed UI-label rules and where they
// moved: 小/中/大 font-size scale → SettingsPanel; 方=かた (person) → Login/Register; 空=から (empty-state,
// currently an unrendered key) → (none needed); 色=いろ (ハイライト色/強調色) → SettingsPanel. The engine
// keeps only stimulus-general readings (OVERRIDE compounds / counters / alignToken), context rules that are
// correct everywhere (何+助動詞→なん, 他の→ほか, 今+表示→いま, 主・副 via the ・ anchor, 〜ている間→あいだ),
// and the domain constant 値=あたい.

/**
 * Align a token's whole-token kana reading to the KANJI runs inside its surface, so
 * ruby sits over kanji only:  食べる → 食(た)べる,  棒グラフ → 棒(ぼう)グラフ,  平均 → 平均(へいきん).
 * Kana runs in the surface are matched (as hiragana) against the reading to bound each
 * kanji run's reading. If the surface can't be reproduced exactly, falls back to a
 * single whole-token ruby (never corrupts the base text).
 * @returns {Array<{text:string}|{base:string,reading:string}>}
 */
export function alignToken(surface, readingHira) {
  const runs = [];
  let i = 0;
  while (i < surface.length) {
    const kanji = isKanjiChar(surface[i]);
    let j = i + 1;
    while (j < surface.length && isKanjiChar(surface[j]) === kanji) j += 1;
    runs.push({ text: surface.slice(i, j), kanji });
    i = j;
  }
  const out = [];
  let r = 0;
  let ok = true;
  for (let idx = 0; idx < runs.length && ok; idx += 1) {
    const run = runs[idx];
    if (!run.kanji) {
      const anchor = kataToHira(run.text);
      if (readingHira.startsWith(anchor, r)) { out.push({ text: run.text }); r += anchor.length; }
      else ok = false;
    } else {
      const next = runs[idx + 1];
      let end;
      if (next) {
        // search for the next kana run's anchor from r+1: a kanji run consumes >=1 mora, so the
        // anchor can not be at r. Starting at r would mis-match when the kanji reading begins with
        // the okurigana's first kana (示し/しめし: the し at index 0 belongs to 示=しめ, not the
        // okurigana), forcing a whole-token fallback that puts ruby over the okurigana.
        const at = readingHira.indexOf(kataToHira(next.text), r + 1);
        end = at;
      } else {
        end = readingHira.length;
      }
      if (end < 0 || end <= r) ok = false;
      else { out.push({ base: run.text, reading: readingHira.slice(r, end) }); r = end; }
    }
  }
  if (ok && r === readingHira.length) return out;
  return [{ base: surface, reading: readingHira }]; // safe whole-token fallback
}

/**
 * Split a string into ordered furigana segments using the morphological tokenizer.
 * (A 2nd argument is accepted and ignored for backward-compat with call sites.)
 * @returns {Array<{text:string}|{base:string,reading:string}>}
 */
function mergePlainSegments(segs) {
  const merged = [];
  for (const s of segs) {
    const last = merged[merged.length - 1];
    if (s.text != null && last && last.text != null) last.text += s.text;
    else merged.push({ ...s });
  }
  return merged;
}

function sliceAnnotatedSegments(text, segs, rangeStart, rangeEnd) {
  const sliced = [];
  let offset = 0;
  for (const seg of segs) {
    const base = seg.base != null ? seg.base : (seg.text || '');
    const start = offset;
    const end = start + base.length;
    offset = end;
    const a = Math.max(start, rangeStart);
    const b = Math.min(end, rangeEnd);
    if (b <= a) continue;
    if (a === start && b === end) sliced.push(seg);
    else sliced.push({ text: text.slice(a, b) });
  }
  return sliced;
}

function annotateFuriganaImpl(text, guardUnverifiedNames) {
  if (typeof text !== 'string' || text.length === 0) return [];
  if (!_tokenizer) return [{ text }]; // not ready → plain, never a guessed reading
  // The engine also runs over uploaded research stimulus (problem text / chart labels / chat), so EVERY
  // correction below is UNIVERSAL — correct in every context. It is either stimulus-general (OVERRIDE
  // compounds / counters / alignToken), a context rule that is correct everywhere (何+助動詞→なん / 他の→ほか
  // / 今+表示→いま / 主・副 via the ・ anchor / 〜ている間→あいだ), or the domain constant 値=あたい. UI-label
  // readings that would be wrong on dynamic text (中/方/空/色) are NOT here — the component supplies them at
  // its render site via <FuriganaText overrides>. There is NO caller flag; a fragment must never be
  // re-tokenized (callers that decorate/split prose annotate the WHOLE string once and project onto ranges —
  // see groupSegmentsByRanges / projectDecoratedText, used by ProblemTextPanel / ChatInterface / InteractiveGraph).
  let tokens;
  try { tokens = _tokenizer.tokenize(text); } catch (e) { return [{ text }]; }
  // IPADIC can lose source code units when an astral symbol is adjacent to an
  // unknown supplementary Han name (e.g. 😀𠮷田さん -> token surfaces 😀𠮷|ん).
  // Every projector below uses exact UTF-16 source offsets, so a non-lossless token
  // stream is unusable: keep the complete source plain rather than delete/change text.
  if (tokens.map((tk) => (tk && tk.surface_form) || '').join('') !== text) return [{ text }];

  let tokenOffset = 0;
  const tokenSpans = tokens.map((tk) => {
    const start = tokenOffset;
    tokenOffset += (tk && tk.surface_form) ? tk.surface_form.length : 0;
    return { start, end: tokenOffset };
  });
  const nameContexts = findNameContexts(text, tokens, tokenSpans);
  if (guardUnverifiedNames) {
    const unsafeNames = nameContexts
      .filter((range) => range.hasUnresolvedToken
        && !range.dictionaryVerified && !range.externallyVerified)
      .sort((a, b) => a.start - b.start);
    if (unsafeNames.length > 0) {
      // First annotate the complete source with name guarding disabled. Slicing that
      // result preserves the original morphological context on BOTH sides of the
      // protected range (前小鳥遊さん keeps 前=ぜん); re-tokenizing prefix/suffix
      // substrings independently would change valid readings at the cut boundary.
      const sourceSegments = annotateFuriganaImpl(text, false);
      const guarded = [];
      let cursor = 0;
      for (const range of unsafeNames) {
        if (range.start < cursor) continue;
        if (range.start > cursor) {
          guarded.push(...sliceAnnotatedSegments(text, sourceSegments, cursor, range.start));
        }
        guarded.push({ text: text.slice(range.start, range.end) });
        const suffixReading = range.suffixReading ?? PERSON_NAME_SUFFIX_READINGS.get(range.suffix);
        if (suffixReading) guarded.push(...alignToken(range.suffix, suffixReading));
        else guarded.push({ text: range.suffix });
        cursor = range.suffixEnd;
      }
      if (cursor < text.length) {
        guarded.push(...sliceAnnotatedSegments(text, sourceSegments, cursor, text.length));
      }
      return mergePlainSegments(guarded);
    }
  }
  const nameContextByStart = new Map(nameContexts.map((range) => [range.start, range]));

  const segs = [];
  let prevSurface = '';
  let prevPrevSurface = ''; // surface two non-blank tokens back (for 主・副: 副 needs 主 before the ・)
  let prevToken = null; // last non-blank token (for POS-conditioned rules, e.g. 間)
  for (let ti = 0; ti < tokens.length; ti += 1) {
    const tk = tokens[ti];
    const surface = tk && tk.surface_form != null ? tk.surface_form : '';
    if (!surface) continue;
    const nameContext = nameContextByStart.get(tokenSpans[ti].start);
    // verified never-guess proper names: render bare (no reading), never kuromoji's guess.
    // Same multi-token concatenation match as the override below.
    let bare = null;
    for (const key of BARE_SURFACES) {
      let acc = '';
      let j = ti;
      while (j < tokens.length && acc.length < key.length) { acc += tokens[j].surface_form || ''; j += 1; }
      if (acc === key) { bare = { key, end: j }; break; }
    }
    if (bare) {
      segs.push({ text: bare.key });
      prevToken = tokens[bare.end - 1];
      prevPrevSurface = prevSurface;
      prevSurface = prevToken.surface_form || '';
      ti = bare.end - 1;
      continue;
    }
    // verified multi-token override first: kuromoji may split a mis-parsed word across
    // tokens (高品質 → 高品|質), so match an OVERRIDE key by the concatenated surface here.
    let ov = null;
    for (const key of OVERRIDE_KEYS) {
      // In an explicit person-name context, prefer the dictionary's human-name
      // reading over a lexical compound override. Only externally verified names
      // such as 伊藤啓 are allowed to override that proper-name reading.
      if (nameContext && nameContext.dictionaryVerified && !nameContext.externallyVerified) break;
      let acc = '';
      let j = ti;
      while (j < tokens.length && acc.length < key.length) { acc += tokens[j].surface_form || ''; j += 1; }
      if (acc === key) { ov = { key, end: j }; break; }
    }
    if (ov) {
      for (const s of alignToken(ov.key, OVERRIDE_READINGS[ov.key])) segs.push(s);
      prevToken = tokens[ov.end - 1];
      prevPrevSurface = prevSurface;
      prevSurface = prevToken.surface_form || '';
      ti = ov.end - 1; // loop will ++ past the consumed tokens
      continue;
    }

    const hasKanji = Array.from(surface).some(isKanjiChar);
    const reading = tk.reading && tk.reading !== '*' ? kataToHira(tk.reading) : null;
    // the next NON-BLANK token: OCR often inserts a space before the disambiguating token ("他 のグラフ" /
    // "何 ですか" / "主 ・ 副"), and a raw tokens[ti+1] would be that blank so the successor rules (他+の /
    // 何+助動詞 / 主+・ / 今+表示) would silently miss and corrupt the reading (Codex R20 #1). prevSurface /
    // prevPrevSurface already skip blanks (updated only on non-blank tokens), so this makes lookahead symmetric.
    let nextTok = null;
    let nextTok2 = null;
    for (let nj = ti + 1; nj < tokens.length; nj += 1) {
      if ((tokens[nj].surface_form || '').trim() === '') continue;
      if (!nextTok) nextTok = tokens[nj];
      else { nextTok2 = tokens[nj]; break; }
    }
    const next = nextTok ? (nextTok.surface_form || '') : '';
    const next2 = nextTok2 ? (nextTok2.surface_form || '') : '';
    if (surface === '値' && reading === 'ね') {
      // DOMAIN reading: in this graph-comprehension tool a standalone 値 is overwhelmingly the "data value"
      // あたい (kuromoji gives the 値段/price ね). This is a PI-domain decision (like 数値=ち): the price idiom
      // 値が張る is rare, non-graph, and indistinguishable from the dominant value sense without semantics, so
      // 値=あたい is applied in every context including uploaded stimulus. A compound 値=ち is untouched.
      segs.push({ base: '値', reading: 'あたい' });
    } else if (surface === '今' && reading === 'こん' && next === '表示') {
      // 今 + 表示 = "now displaying" → いま (universal: 今 immediately before 表示 is the "now" sense in any
      // text). Stimulus prefix-今 (今学期=こんがっき / 今世紀) keeps こん because its next token is not 表示;
      // 今日/今週 are single tokens read correctly.
      segs.push({ base: '今', reading: 'いま' });
    } else if (surface === '他' && reading === 'た' && next === 'の') {
      // 他 + の = ほか (universal: 他の… is always ほかの…). 他+noun (他県=たけん / 他人=たにん) keeps た;
      // その他 is its own token (そのた), never matched here.
      segs.push({ base: '他', reading: 'ほか' });
    } else if (surface === '主' && next === '・' && next2 === '副') {
      // 主・副 pair: 主 before "・副" = しゅ. Requires the FULL 主・副 (the non-blank token after the ・ is 副), so
      // apposition using ・ — "この家の主・田中さん" (主 = あるじ, "the master") — is NOT corrupted (Codex R21 #1).
      // Mirrors 副's prevPrev=主 anchor. 主モデル/主判定 already しゅ, 主に=おも.
      segs.push({ base: '主', reading: 'しゅ' });
    } else if (surface === '副' && reading === 'そい' && prevSurface === '・' && prevPrevSurface === '主') {
      // 主・副 pair: 副 after "主・" = ふく. IPADIC mis-parses a lone 副 as the place-name 固有名詞 そい. Requires
      // the FULL 主・副 context (the token before the ・ is 主), so a leading LIST BULLET — "・副の人口" — does NOT
      // fire and the place-name 副の人口=そい is untouched (Codex R17 #3, tightening R16 #6). Mirrors 主's ・-anchor.
      segs.push({ base: '副', reading: 'ふく' });
    } else if (surface === '間' && reading === 'ま' && prevToken
        && prevToken.pos === '動詞' && prevToken.pos_detail_1 === '非自立') {
      // 〜ている間 = あいだ ("while doing"): the token before 間 is an auxiliary verb (動詞-非自立: いる/ある in
      // 〜ている/〜てある). Universal — kuromoji gives ま but 〜ている間 is always あいだ (e.g. 読んでいる間). Every
      // OTHER lone 間[ま] is left to kuromoji, having no reliable morphological cue to disambiguate: …の間 (a room
      // 奥の間/桜の間 vs 直線の間, R16 #4), an idiomatic 自立-verb+間 (瞬く間), and a SPACED noun+間 — an inter- AI間
      // (=かん) is indistinguishable from a room 客間/六畳間 (=ま), Codex R18 #1. Joined compounds (時間/区間/AI間,
      // no space) are single tokens kuromoji already reads correctly.
      segs.push({ base: '間', reading: 'あいだ' });
    } else if (surface === '何' && nextTok && nextTok.pos === '助動詞') {
      // 何 + 助動詞 (copula です/だ/でし/でした) → なん (何ですか). POS-gated, so a 助詞 that merely BEGINS with
      // だ — だけ in 何だけ (Codex R16 #7) — does not match and 何だけ keeps kuromoji's なに. 何を/何が stay なに;
      // 何割 is なん natively (何=名詞-数).
      segs.push({ base: '何', reading: 'なん' });
    } else if (hasKanji && surface.includes('月')
        && (NUMERIC_TOKEN_RE.test(prevSurface) || /[0-9０-９]月/.test(surface))) {
      // ANY 月 right after a number — a separate token after half-width digits (1月 →
      // [1][月]), a 月-compound (1月末→月末, 12月頃→月頃), OR a single token whose surface
      // carries a leading full-width digit (１月 / １月後, IPADIC keeps it whole) — has a
      // context-unresolvable reading (がつ/げつ/つき/かげつ), so bare the whole token.
      segs.push({ text: surface });
    } else if (hasKanji && surface.length === 1 && NUMERIC_TOKEN_RE.test(prevSurface)) {
      // lone counter kanji after a number
      if (COUNTER_READINGS[surface]) {
        segs.push({ base: surface, reading: COUNTER_READINGS[surface] }); // 年ねん/時じ/件けん/個こ/…
      } else {
        // irregular OR sense-ambiguous counter → bare, never a wrong reading. 分 in particular is unresolvable
        // from digit+分 alone — 3分 = さんぷん ("3 minutes") vs 3分の1 = さんぶんのいち ("one third"), and an
        // OCR-spaced "3 分の1" makes it worse (Codex R19 #1). 日/本/人 are likewise digit-dependent → bare.
        segs.push({ text: surface });
      }
    } else if (hasKanji && reading && reading !== surface) {
      for (const s of alignToken(surface, reading)) segs.push(s);
    } else {
      segs.push({ text: surface });
    }
    // keep prevSurface/prevToken on the last NON-blank token so a counter still sees its number
    // across an intervening space token (kuromoji emits ' ' as a 記号-空白 token: "44 問" → [44][ ][問]).
    if (surface.trim() !== '') { prevPrevSurface = prevSurface; prevSurface = surface; prevToken = tk; }
  }
  // merge consecutive plain segments so callers see clean runs
  return mergePlainSegments(segs);
}

export function annotateFurigana(text) {
  return annotateFuriganaImpl(text, true);
}

// Default meta-combiner for an ATOMIC ruby segment overlapping ≥2 ranges: keep the max-overlap range's meta,
// preferring a decorated (non-null) one on ties. Used by ProblemTextPanel, whose meta is a keyword IDENTITY
// (two keywords inside one dictionary word can't both be independently clickable — one must win).
function maxOverlapMeta(over, start, end) {
  const score = (rg) => Math.min(end, rg.end) - Math.max(start, rg.start);
  let best = over[0];
  for (const rg of over) {
    const d = score(rg) - score(best);
    if (d > 0 || (d === 0 && rg.meta && !best.meta)) best = rg;
  }
  return best.meta;
}

/**
 * Annotate `text` ONCE and partition its segments into groups aligned to `ranges` (decoration spans such as
 * keyword highlights or markdown emphasis). Each range is `{ start, end, meta }` in CHARACTER offsets.
 *
 * A PLAIN-TEXT segment is ALWAYS tiled at the range boundaries it crosses — so a decoration that begins or ends
 * mid-run is neither dropped nor expanded to the whole run (Codex R17 #1: a keyword inside a long kana run stays
 * just that keyword). An ATOMIC reading segment (a kanji word rendered as one ruby, e.g. 数値=すうち) can NOT be
 * split without corrupting its reading, so it is kept whole; if it overlaps ≥2 ranges their metas are combined
 * by `combineMeta` (default = max-overlap, prefer decorated) so NONE is silently dropped — projectDecoratedText
 * passes a combiner that OR-s bold/hl, so 数値 keeps BOTH a bold on 数 and a highlight on 値 (Codex R17 #2).
 * Consecutive segments carrying the SAME meta merge. This lets a caller render DECORATED prose without ever
 * re-tokenizing a fragment (which would strip the neighbours a reading rule needs).
 * @param {(metas: any[]) => any} [combineMeta] combine the metas of ranges overlapping one atomic segment
 * @returns {Array<{ meta: any|null, segs: Array<{text:string}|{base:string,reading:string}> }>}
 */
export function groupSegmentsByRanges(text, ranges, combineMeta) {
  const segs = annotateFurigana(text);
  const rs = Array.isArray(ranges) ? ranges : [];
  const groups = [];
  const emit = (meta, seg) => {
    const last = groups[groups.length - 1];
    if (last && last.meta === meta) last.segs.push(seg); // same meta ref: null gaps / one leaf / one keyword
    else groups.push({ meta, segs: [seg] });
  };
  let off = 0;
  for (const seg of segs) {
    const s = seg.base != null ? seg.base : (seg.text || '');
    const start = off;
    const end = off + s.length;
    off = end;
    const over = rs.filter((rg) => start < rg.end && end > rg.start);
    // Identity text is intentionally rendered WITHOUT ruby because a participant's name reading is not
    // spelling-derivable. That makes it safe (and necessary) to split an otherwise atomic dictionary ruby at
    // exact selection boundaries: selecting 佐 out of 佐々木 must highlight/show only 佐, not expand to the
    // whole surname. Any non-identity remainder of the same atomic token is also left plain; inventing a
    // partial reading would be worse than omitting one.
    const splitIdentityAtomic = seg.text == null
      && over.some((rg) => rg.meta && rg.meta.isIdentity);
    if (seg.text != null || splitIdentityAtomic) {
      // PLAIN text → tile at every overlapping range's intersection (lossless split; handles 0/1/many ranges)
      const sorted = [...over].sort((a, b) => a.start - b.start);
      let cur = start;
      for (const rg of sorted) {
        const a = Math.max(cur, rg.start);
        if (a > cur) emit(null, { text: text.slice(cur, a) }); // gap before this range
        const b = Math.min(end, rg.end);
        if (b > a) { emit(rg.meta, { text: text.slice(a, b) }); cur = b; }
      }
      if (cur < end) emit(null, { text: text.slice(cur, end) });
    } else if (over.length === 0) {
      emit(null, seg);
    } else if (over.length === 1) {
      emit(over[0].meta, seg);
    } else {
      // ATOMIC ruby overlapping ≥2 ranges — can't split; combine every overlapping meta so none is dropped
      emit(combineMeta ? combineMeta(over.map((rg) => rg.meta)) : maxOverlapMeta(over, start, end), seg);
    }
  }
  return groups;
}

/**
 * Strip the supported `**bold**` markup while keeping caller-protected ranges (participant identities) literal.
 * Both the returned bold ranges and protected ranges use offsets in the returned plain string. A username may
 * itself contain `**`; a markdown match that touches a protected range is therefore not formatting and is kept
 * byte-for-byte. Shared by message projection and selection chips so both use one coordinate space.
 */
export function parseDecoratedText(rawText, protectedRanges = []) {
  if (typeof rawText !== 'string') return { plain: '', bolds: [], protectedRanges: [] };
  const protectedRaw = (Array.isArray(protectedRanges) ? protectedRanges : [])
    .map((rg) => ({ start: Math.max(0, rg.start), end: Math.min(rawText.length, rg.end) }))
    .filter((rg) => Number.isFinite(rg.start) && Number.isFinite(rg.end) && rg.end > rg.start);
  const accepted = [];
  const boldRe = /\*\*([^*]+)\*\*/g;
  let match;
  while ((match = boldRe.exec(rawText)) !== null) {
    const matchEnd = match.index + match[0].length;
    let overlapsProtected = false;
    for (const rg of protectedRaw) {
      if (match.index < rg.end && matchEnd > rg.start) {
        overlapsProtected = true;
        break;
      }
    }
    if (overlapsProtected) continue;
    accepted.push(match);
  }

  let plain = '';
  const bolds = [];
  let last = 0;
  accepted.forEach((m) => {
    plain += rawText.slice(last, m.index);
    const start = plain.length;
    plain += m[1];
    bolds.push([start, plain.length]);
    last = m.index + m[0].length;
  });
  plain += rawText.slice(last);

  const toPlainOffset = (rawOffset) => {
    let removed = 0;
    accepted.forEach((m) => {
      const close = m.index + m[0].length - 2;
      [[m.index, m.index + 2], [close, close + 2]].forEach(([a, b]) => {
        removed += Math.max(0, Math.min(rawOffset, b) - a);
      });
    });
    return rawOffset - removed;
  };
  const normalizedProtected = protectedRaw.map((rg) => ({
    start: toPlainOffset(rg.start),
    end: toPlainOffset(rg.end),
  }));
  return { plain, bolds, protectedRanges: normalizedProtected };
}

/**
 * Turn a raw AI/markdown string into decoration groups for a furigana-aware renderer WITHOUT re-tokenizing
 * fragments: strip `**bold**`, locate every `highlights` phrase, cut the plain text into leaf ranges at all
 * bold/highlight boundaries (meta `{ bold, hl }`), then project the whole-string annotation onto them. With
 * `ruby` false the segments are plain text (no reading). Shared by ChatInterface (chat bodies) and
 * InteractiveGraph (stage explanations) so both apply the SAME whole-string projection (Codex R16 #2) —
 * a bold/highlight boundary that isolates a context-sensitive kanji (箱の**中**) is never tokenized alone.
 * @returns {Array<{ range: object, meta: {bold:boolean, hl:boolean}, segs: Array<{text:string}|{base:string,reading:string}> }>}
 */
export function projectDecoratedText(rawText, highlights, ruby, identityRanges = []) {
  if (typeof rawText !== 'string' || rawText.length === 0) return [];
  // 1. strip chrome **bold** while retaining identity literals, and normalize both kinds of ranges into
  // plain-text offsets. User-entered `**` must never be mistaken for our markdown (Codex R23 #4).
  const parsed = parseDecoratedText(rawText, identityRanges);
  const { plain, bolds } = parsed;
  // 2. highlight ranges — every occurrence of each phrase (plain-text offsets)
  const hls = [];
  (Array.isArray(highlights) ? highlights : []).forEach((h) => {
    if (!h) return;
    let idx = plain.indexOf(h);
    while (idx >= 0) { hls.push([idx, idx + h.length]); idx = plain.indexOf(h, idx + h.length); }
  });
  // 3. cut into leaf ranges at every bold/highlight boundary
  const identities = parsed.protectedRanges;
  const bounds = new Set([0, plain.length]);
  [...bolds, ...hls].forEach(([a, b]) => { bounds.add(a); bounds.add(b); });
  identities.forEach(({ start, end }) => { bounds.add(start); bounds.add(end); });
  const cuts = [...bounds].sort((a, b) => a - b);
  const ranges = [];
  for (let i = 0; i < cuts.length - 1; i += 1) {
    const a = cuts[i];
    const b = cuts[i + 1];
    if (b <= a) continue;
    const bold = bolds.some(([x, y]) => x <= a && b <= y);
    const hl = hls.some(([x, y]) => x <= a && b <= y);
    const isIdentity = identities.some((rg) => rg.start <= a && b <= rg.end);
    // An UNDECORATED leaf carries meta=null so groupSegmentsByRanges can tell it from a decorated one when a
    // reading segment straddles a boundary (数**値**: the ruby can't split, so the whole 数値 must follow the
    // BOLD leaf, not the plain one) and so consumers' `g.meta && g.meta.bold` guard skips it (Codex R16 #3).
    ranges.push({
      start: a,
      end: b,
      meta: (bold || hl || isIdentity) ? { bold, hl, ...(isIdentity ? { isIdentity: true } : {}) } : null,
    });
  }
  // 4. annotate the whole plain text ONCE and project onto the leaf ranges. A kanji word straddling two leaves
  // (数**値** + a highlight on 値) keeps BOTH decorations because the combiner OR-s the bold/hl flags (R17 #2).
  const combineMeta = (metas) => {
    const bold = metas.some((mm) => mm && mm.bold);
    const hl = metas.some((mm) => mm && mm.hl);
    const isIdentity = metas.some((mm) => mm && mm.isIdentity);
    return (bold || hl || isIdentity) ? { bold, hl, ...(isIdentity ? { isIdentity: true } : {}) } : null;
  };
  if (ruby) return groupSegmentsByRanges(plain, ranges, combineMeta);
  return ranges.map((r) => ({ meta: r.meta, segs: [{ text: plain.slice(r.start, r.end) }] }));
}

// A unique marker the caller interpolates in place of the username (see splitInterpolatedIdentity). U+FFFC
// OBJECT REPLACEMENT CHARACTER never appears in participant text or i18n copy, so splitting on it can not
// collide with fixed chrome.
export const IDENTITY_MARKER = '￼';

/**
 * Replace every identity marker with the real name and retain the exact output
 * ranges occupied by the identity. Later renderers use those ranges to keep the
 * name bare while projecting ruby / bold / highlight over the reconstructed text
 * as one string. This avoids both name-vs-chrome string collisions and decoration
 * loss when a selection crosses the identity boundary.
 * @returns {{ text: string, identityRanges: Array<{start:number,end:number}> }}
 */
export function interpolateIdentityRanges(marked, identity, marker = IDENTITY_MARKER) {
  if (typeof marked !== 'string' || marked.length === 0) return { text: '', identityRanges: [] };
  if (!marker || !marked.includes(marker)) return { text: marked, identityRanges: [] };
  const replacement = identity != null ? String(identity) : '';
  const identityRanges = [];
  let text = '';
  marked.split(marker).forEach((part, i, parts) => {
    text += part;
    if (i < parts.length - 1) {
      const start = text.length;
      text += replacement;
      if (replacement.length > 0) identityRanges.push({ start, end: text.length });
    }
  });
  return { text, identityRanges };
}

/**
 * Structure a message that interpolates a username into ordered `{ text, isIdentity }` parts, splitting on the
 * TRUE interpolation position — the caller substitutes IDENTITY_MARKER for the name, so this splits on the
 * MARKER, not on the name string. That is why a one-character name 先 can not break the fixed honorific 先生,
 * and a name equal to a chrome word (先生) can not strip that chrome's reading (Codex R21 #4). The name may be
 * interpolated more than once ("{{name}}さん … {{name}}の先生"), so every marker occurrence is split out. The
 * caller renders the non-identity parts furigana'd and the identity parts BARE — a user-entered proper name's
 * reading is not derivable from its spelling (小鳥遊 = たかなし), so it must never be furigana-guessed (R19/R20).
 * @param {string} marked   the message with IDENTITY_MARKER already substituted for the name
 * @param {string} identity the real name to place back into the identity slots (rendered bare)
 * @returns {Array<{ text: string, isIdentity: boolean }>}
 */
export function splitInterpolatedIdentity(marked, identity, marker = IDENTITY_MARKER) {
  if (typeof marked !== 'string' || marked.length === 0) return [];
  if (!marker || !marked.includes(marker)) return [{ text: marked, isIdentity: false }];
  const out = [];
  marked.split(marker).forEach((part, i, arr) => {
    if (part) out.push({ text: part, isIdentity: false });
    if (i < arr.length - 1) out.push({ text: identity != null ? String(identity) : '', isIdentity: true });
  });
  return out;
}

/** How many kanji ended up WITHOUT a reading (unknown to the tokenizer, or not ready). */
export function countUnreadKanji(text) {
  if (typeof text !== 'string') return 0;
  let unread = 0;
  for (const seg of annotateFurigana(text)) {
    if (seg.text) for (const ch of seg.text) if (isKanjiChar(ch)) unread += 1;
  }
  return unread;
}

/** True if any segment carries a reading (i.e. furigana was actually added). */
export function hasFurigana(segments) {
  return Array.isArray(segments) && segments.some((s) => s && typeof s.reading === 'string');
}
